import React from 'react';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Button, Radio, Tooltip } from 'antd';
import {
    CORE_SIZE__SINGLEMODE,
    CORE_SIZE__MULTIMODE_50,
    MTJ__SINGLE_END_A_THEN_END_B,
    MTJ__SINGLE_END_A,
    MTJ__SINGLE_END_B,
    MTJ__DOUBLE,
    TEST_PLAN_TEMPLATE__SIMPLEX,
    TJ_TYPE__AUTO,
    TERMINATION_TYPE__BARE_FIBER
} from '@shared/constants/constants';
import singleMtjYellow from '../../../images/SM-1MTJ.png';
import doubleMtjYellow from '../../../images/SM-2MTJ.png';

import singleMtjBlue from '../../../images/MM50-1MTJ.png';
import doubleMtjBlue from '../../../images/MM50-2MTJ.png';

import singleMtjRed from '../../../images/MM62-1MTJ.png';
import doubleMtjRed from '../../../images/MM62-2MTJ.png';

import './SetupPage.scss';
import { Equipment, Assembly, WizardState } from '@shared/types/test-plan/types';
import { deriveMtjConfiguration } from '../lib/mtjConfiguration';

interface SetupPageProps {
    wizard: WizardState;
    updateWizardData: StateUpdater<WizardState>;
    updateAssembly: StateUpdater<Assembly>;
    updateEquipment: StateUpdater<Equipment>;
}

export class SetupPage extends React.PureComponent<SetupPageProps> {
    hasSideATest() {
        return this.props.wizard.assembly.insertionLossSideA || this.props.wizard.assembly.returnLossSideA;
    }

    hasSideBTest() {
        return this.props.wizard.assembly.insertionLossSideB || this.props.wizard.assembly.returnLossSideB;
    }

    hasReturnLossTotalTest() {
        return this.props.wizard.assembly.returnLossTotal;
    }

    hasInsertionLossTotalTest() {
        return this.props.wizard.assembly.insertionLossTotal;
    }

    hasInsertionLossTest() {
        return (
            this.props.wizard.assembly.insertionLossSideA ||
            this.props.wizard.assembly.insertionLossSideB ||
            this.props.wizard.assembly.insertionLossTotal
        );
    }

    hasReturnLossEndAAndEndBTests() {
        return this.props.wizard.assembly.returnLossSideA && this.props.wizard.assembly.returnLossSideB;
    }

    isMTJConfigurationVisible() {
        return this.hasSideATest() || this.hasSideBTest() || this.hasReturnLossTotalTest() || this.hasInsertionLossTotalTest();
    }

    isSingleMTJEndAVisible() {
        const case1 =
            (this.hasSideATest() || this.hasReturnLossTotalTest()) && !this.hasInsertionLossTotalTest() && !this.hasSideBTest();

        return case1;
    }

    isSingleMTJEndBVisible() {
        const case1 =
            (this.hasSideBTest() || this.hasReturnLossTotalTest()) && !this.hasInsertionLossTotalTest() && !this.hasSideATest();

        return case1 && this.props.wizard.assembly.terminationTypeEndB !== TERMINATION_TYPE__BARE_FIBER;
    }

    isSingleMTJBothEndsVisible() {
        const case1 = this.hasSideATest() && this.hasSideBTest() && !this.hasInsertionLossTotalTest();

        const case2 =
            this.hasReturnLossTotalTest() && !this.hasInsertionLossTotalTest() && !this.hasSideATest() && !this.hasSideBTest();

        return case1 || case2;
    }

    isDualMTJVisible() {
        const case1 = this.hasInsertionLossTotalTest();

        const case2 = !this.hasInsertionLossTest() && this.hasReturnLossEndAAndEndBTests();

        const case3 =
            this.hasReturnLossTotalTest() && !this.hasInsertionLossTotalTest() && !this.hasSideATest() && !this.hasSideBTest();

        return (case1 || case2 || case3) && this.props.wizard.assembly.terminationTypeEndB !== TERMINATION_TYPE__BARE_FIBER;
    }

    // Apply a test-type change (the IL/RL buttons) and re-derive the MTJ configuration so it stays
    // consistent with the selected tests. This replaces the reactive derivation that used to live in
    // the wizard's componentDidUpdate — the IL/RL buttons here are the only place these flags change.
    applyTestSelection = (assemblyChanges: Partial<Assembly>, extraWizardData: Partial<WizardState> = {}) => {
        const nextAssembly = { ...this.props.wizard.assembly, ...assemblyChanges };

        this.props.updateWizardData({
            ...extraWizardData,
            assembly: nextAssembly,
            equipment: {
                ...this.props.wizard.equipment,
                mtjConfiguration: deriveMtjConfiguration(nextAssembly)
            }
        });
    };

    handleChangeMjtConfiguration = (mtjConfiguration) => {
        const { wizard } = this.props;
        const isSingleMTJOneEndOnly = mtjConfiguration === MTJ__SINGLE_END_A || mtjConfiguration === MTJ__SINGLE_END_B;
        const isBareFiber = wizard.assembly.terminationTypeEndB === TERMINATION_TYPE__BARE_FIBER;

        // Equipment
        const newEquipment = {
            mtjConfiguration: mtjConfiguration,
            singleTestBlock: !isSingleMTJOneEndOnly && !isBareFiber
        };

        // Wizard Data
        let newWizardData;

        if (mtjConfiguration === MTJ__SINGLE_END_A || mtjConfiguration === MTJ__SINGLE_END_B) {
            newWizardData = {
                hybridAssembly: false
            };
        } else {
            newWizardData = {};
        }

        // Assembly
        let newAssemblyData = {};

        if (mtjConfiguration === MTJ__SINGLE_END_B) {
            newAssemblyData = {
                polaritySequence: 1
            };
        } else if (this.props.wizard.equipment.mtjConfiguration === MTJ__SINGLE_END_B) {
            newAssemblyData = {
                polaritySequence: 2
            };
        }

        let testPlan = this.props.wizard.testPlan;

        newWizardData = {
            ...newWizardData,
            equipment: {
                ...this.props.wizard.equipment,
                ...newEquipment
            },
            assembly: {
                ...this.props.wizard.assembly,
                ...newAssemblyData
            },
            testPlan: testPlan
        };

        this.props.updateWizardData(newWizardData);
    };

    render() {
        return (
            <React.Fragment>
                <div className="page setup-page">
                    <div className="test-types-line">
                        <div>
                            <div data-testid="ilTestSelection">
                                {!this.props.wizard.assembly.insertionLossSideA &&
                                    !this.props.wizard.assembly.insertionLossSideB &&
                                    !this.props.wizard.assembly.insertionLossTotal &&
                                    !this.props.wizard.assembly.returnLossSideA &&
                                    !this.props.wizard.assembly.returnLossSideB &&
                                    !this.props.wizard.assembly.returnLossTotal &&
                                    !this.props.wizard.assembly.testPolarity && (
                                        <div style={{ color: '#e60013' }}> Please select a test type</div>
                                    )}
                                <div className="title">Insertion Loss: </div>
                                <React.Fragment>
                                    <Button
                                        type={this.props.wizard.assembly.insertionLossSideA ? 'primary' : 'default'}
                                        onClick={() => {
                                            this.applyTestSelection({
                                                insertionLossSideA: !this.props.wizard.assembly.insertionLossSideA,
                                                insertionLossTotal: false
                                            });
                                        }}
                                    >
                                        End A
                                    </Button>
                                    <Button
                                        disabled={
                                            this.props.wizard.assembly.terminationTypeEndB === TERMINATION_TYPE__BARE_FIBER
                                        }
                                        type={this.props.wizard.assembly.insertionLossSideB ? 'primary' : 'default'}
                                        onClick={() => {
                                            this.applyTestSelection({
                                                insertionLossSideB: !this.props.wizard.assembly.insertionLossSideB,
                                                insertionLossTotal: false
                                            });
                                        }}
                                    >
                                        End B
                                    </Button>
                                    &nbsp;&nbsp; OR &nbsp;&nbsp;
                                    <Button
                                        type={this.props.wizard.assembly.insertionLossTotal ? 'primary' : 'default'}
                                        disabled={
                                            this.props.wizard.assembly.terminationTypeEndB === TERMINATION_TYPE__BARE_FIBER
                                        }
                                        onClick={() => {
                                            this.applyTestSelection(
                                                {
                                                    insertionLossTotal: !this.props.wizard.assembly.insertionLossTotal,
                                                    insertionLossSideB: false,
                                                    insertionLossSideA: false
                                                },
                                                { showDutLength: false }
                                            );
                                        }}
                                    >
                                        Total
                                    </Button>
                                </React.Fragment>
                            </div>
                            <div data-testid="rlTestSelection">
                                <div className="title">Return Loss: </div>
                                <Form.Item>
                                    <React.Fragment>
                                        <Button
                                            type={this.props.wizard.assembly.returnLossSideA ? 'primary' : 'default'}
                                            onClick={() => {
                                                this.applyTestSelection({
                                                    returnLossSideA: !this.props.wizard.assembly.returnLossSideA
                                                });
                                            }}
                                        >
                                            End A
                                        </Button>
                                        <Button
                                            disabled={
                                                this.props.wizard.assembly.terminationTypeEndB === TERMINATION_TYPE__BARE_FIBER
                                            }
                                            type={this.props.wizard.assembly.returnLossSideB ? 'primary' : 'default'}
                                            onClick={() => {
                                                this.applyTestSelection({
                                                    returnLossSideB: !this.props.wizard.assembly.returnLossSideB
                                                });
                                            }}
                                        >
                                            End B
                                        </Button>

                                        <Button
                                            type={this.props.wizard.assembly.returnLossTotal ? 'primary' : 'default'}
                                            disabled={this.props.wizard.assembly.coreSize !== CORE_SIZE__SINGLEMODE}
                                            onClick={() => {
                                                this.props.updateAssembly({
                                                    returnLossTotal: !this.props.wizard.assembly.returnLossTotal
                                                });
                                            }}
                                        >
                                            Total
                                        </Button>

                                        <Button
                                            type={this.props.wizard.showDutLength ? 'primary' : 'default'}
                                            disabled={
                                                (this.props.wizard.equipment.mtjConfiguration === MTJ__DOUBLE &&
                                                    this.props.wizard.equipment.tj2 === TJ_TYPE__AUTO) ||
                                                (!this.props.wizard.assembly.returnLossSideA &&
                                                    !this.props.wizard.assembly.returnLossSideB &&
                                                    !this.props.wizard.assembly.returnLossTotal)
                                            }
                                            onClick={() => {
                                                if (this.props.wizard.ilComp.enable && this.props.wizard.showDutLength) {
                                                    this.props.updateWizardData((prevState) => ({
                                                        showDutLength: false,
                                                        ilComp: { ...prevState.ilComp, enable: false }
                                                    }));
                                                }

                                                this.props.updateWizardData({
                                                    showDutLength: !this.props.wizard.showDutLength
                                                });
                                            }}
                                        >
                                            Length
                                        </Button>
                                    </React.Fragment>
                                </Form.Item>
                            </div>

                            {this.props.wizard.testPlanTemplate !== TEST_PLAN_TEMPLATE__SIMPLEX && (
                                <div>
                                    <div className="title">Polarity: </div>

                                    <Radio.Group
                                        buttonStyle="solid"
                                        defaultValue={this.props.wizard.assembly.testPolarity}
                                        onChange={(e) => {
                                            this.props.updateAssembly({ testPolarity: e.target.value });
                                        }}
                                    >
                                        <Radio.Button value={true}>Enabled</Radio.Button>
                                        <Radio.Button value={false}>Disabled</Radio.Button>
                                    </Radio.Group>
                                </div>
                            )}
                            <React.Fragment>
                                {this.props.wizard.assembly.testPolarity && (
                                    <div>
                                        <div className="title">Polarity Sequence:</div>
                                        <Radio.Group
                                            buttonStyle="solid"
                                            value={this.props.wizard.assembly.polaritySequence}
                                            disabled={this.props.wizard.equipment.mtjConfiguration === MTJ__SINGLE_END_B}
                                            onChange={(e) => this.props.updateAssembly({ polaritySequence: e.target.value })}
                                        >
                                            <Tooltip
                                                placement="top"
                                                title="Polarity tests are run independently of IL/RL. Ideal for qualifying cable polarity at a separate workstation"
                                            >
                                                <Radio.Button value={1}>Separate</Radio.Button>
                                            </Tooltip>
                                            <Tooltip
                                                placement="top"
                                                title="Polarity test will be run automatically just before IL/RL."
                                            >
                                                <Radio.Button value={2}>Before IL/RL</Radio.Button>
                                            </Tooltip>
                                            <Tooltip
                                                placement="top"
                                                title="Requires RD-SP. Polarity test will be run concurrently (together) with IL/RL test.."
                                            >
                                                <Radio.Button value={3}>With IL/RL</Radio.Button>
                                            </Tooltip>
                                        </Radio.Group>
                                    </div>
                                )}
                            </React.Fragment>

                            <React.Fragment>
                                <div>
                                    <div className="title">Test Order:</div>
                                    <Radio.Group
                                        buttonStyle="solid"
                                        value={this.props.wizard.equipment.singleTestBlock}
                                        disabled={
                                            this.props.wizard.equipment.mtjConfiguration !== MTJ__SINGLE_END_A_THEN_END_B ||
                                            this.props.wizard.assembly.terminationTypeEndB === TERMINATION_TYPE__BARE_FIBER
                                        }
                                        onChange={(e) => this.props.updateEquipment({ singleTestBlock: e.target.value })}
                                    >
                                        {!this.props.wizard.assembly.testPolarity ? (
                                            <React.Fragment>
                                                {' '}
                                                <Tooltip placement="top" title="Test End A of entire batch, then test End B">
                                                    <Radio.Button value={false}>AA...BB</Radio.Button>
                                                </Tooltip>
                                                <Tooltip placement="top" title="Test both End A and End B before continuing">
                                                    <Radio.Button value={true}>AB...AB</Radio.Button>
                                                </Tooltip>
                                            </React.Fragment>
                                        ) : this.props.wizard.assembly.polaritySequence === 1 ? (
                                            <React.Fragment>
                                                {' '}
                                                <Tooltip placement="top" title="Test End A of entire batch, then test End B">
                                                    <Radio.Button value={false}>AA...BB</Radio.Button>
                                                </Tooltip>
                                                <Tooltip placement="top" title="Test both End A and End B before continuing">
                                                    <Radio.Button value={true}>AB...AB</Radio.Button>
                                                </Tooltip>
                                            </React.Fragment>
                                        ) : (
                                            <React.Fragment>
                                                <Tooltip placement="top" title="Test End A of entire batch, then test End B">
                                                    <Radio.Button value={false}>PAPA...BB</Radio.Button>
                                                </Tooltip>
                                                <Tooltip placement="top" title="Test both End A and End B before continuing">
                                                    <Radio.Button value={true}> PAB... PAB</Radio.Button>
                                                </Tooltip>
                                            </React.Fragment>
                                        )}
                                    </Radio.Group>
                                </div>
                            </React.Fragment>
                        </div>

                        <div>
                            <div>
                                {this.isMTJConfigurationVisible() && (
                                    <React.Fragment>
                                        <div className="section" data-testid="mtjConfigurationSection">
                                            <div className="title">MTJ Configuration:</div>
                                            <div className="mtj-selection">
                                                {this.isSingleMTJEndAVisible() && (
                                                    <Tooltip placement="right" title="Test End A">
                                                        <div
                                                            data-testid="selectMtjEndA"
                                                            className={
                                                                'mtj-a' +
                                                                (this.props.wizard.equipment.mtjConfiguration ===
                                                                MTJ__SINGLE_END_A
                                                                    ? ' selected'
                                                                    : '')
                                                            }
                                                            onClick={() => {
                                                                this.handleChangeMjtConfiguration(MTJ__SINGLE_END_A);
                                                            }}
                                                        >
                                                            <div>
                                                                <div className="mtj-title">Single MTJ (End A)</div>
                                                                <div className="mtj-1">MTJ1</div>
                                                                <div className="dut">DUT</div>
                                                                <div className="end-a">A</div>
                                                                <div className="end-b">B</div>
                                                                <img
                                                                    src={
                                                                        this.props.wizard.assembly.coreSize ===
                                                                        CORE_SIZE__SINGLEMODE
                                                                            ? singleMtjYellow
                                                                            : this.props.wizard.assembly.coreSize ===
                                                                                CORE_SIZE__MULTIMODE_50
                                                                              ? singleMtjBlue
                                                                              : singleMtjRed
                                                                    }
                                                                    alt="Single MTJ end A"
                                                                />
                                                            </div>
                                                        </div>
                                                    </Tooltip>
                                                )}
                                                {this.isSingleMTJEndBVisible() && (
                                                    <Tooltip placement="right" title="Test End B">
                                                        <div
                                                            data-testid="selectMtjEndB"
                                                            className={
                                                                'mtj-b' +
                                                                (this.props.wizard.equipment.mtjConfiguration ===
                                                                MTJ__SINGLE_END_B
                                                                    ? ' selected'
                                                                    : '')
                                                            }
                                                            onClick={() => {
                                                                this.handleChangeMjtConfiguration(MTJ__SINGLE_END_B);
                                                            }}
                                                        >
                                                            <div>
                                                                <div className="mtj-title">Single MTJ (End B)</div>
                                                                <div className="mtj-1">MTJ1</div>
                                                                <div className="dut">DUT</div>
                                                                <div className="end-a">A</div>
                                                                <div className="end-b">B</div>
                                                                <img
                                                                    src={
                                                                        this.props.wizard.assembly.coreSize ===
                                                                        CORE_SIZE__SINGLEMODE
                                                                            ? singleMtjYellow
                                                                            : this.props.wizard.assembly.coreSize ===
                                                                                CORE_SIZE__MULTIMODE_50
                                                                              ? singleMtjBlue
                                                                              : singleMtjRed
                                                                    }
                                                                    alt="Single MTJ end B"
                                                                />
                                                            </div>
                                                        </div>
                                                    </Tooltip>
                                                )}
                                                {this.isSingleMTJBothEndsVisible() && (
                                                    <Tooltip placement="right" title="Test End A and End B separately">
                                                        <div
                                                            data-testid="selectMtjEndAB"
                                                            className={
                                                                'mtj-a-then-b' +
                                                                (this.props.wizard.equipment.mtjConfiguration ===
                                                                MTJ__SINGLE_END_A_THEN_END_B
                                                                    ? ' selected'
                                                                    : '')
                                                            }
                                                            onClick={() => {
                                                                this.handleChangeMjtConfiguration(MTJ__SINGLE_END_A_THEN_END_B);
                                                            }}
                                                        >
                                                            <div>
                                                                <div className="mtj-title">Single MTJ (Both Ends)</div>
                                                                <div className="mtj-1">MTJ1</div>
                                                                <div className="dut">DUT</div>
                                                                <div className="end-a">A</div>
                                                                <div className="end-b">B</div>
                                                                <img
                                                                    src={
                                                                        this.props.wizard.assembly.coreSize ===
                                                                        CORE_SIZE__SINGLEMODE
                                                                            ? singleMtjYellow
                                                                            : this.props.wizard.assembly.coreSize ===
                                                                                CORE_SIZE__MULTIMODE_50
                                                                              ? singleMtjBlue
                                                                              : singleMtjRed
                                                                    }
                                                                    alt="Single MTJ end A"
                                                                />
                                                            </div>

                                                            <div>
                                                                <div className="mtj-1">MTJ1</div>
                                                                <div className="dut">DUT</div>
                                                                <div className="end-a">A</div>
                                                                <div className="end-b">B</div>
                                                                <img
                                                                    src={
                                                                        this.props.wizard.assembly.coreSize ===
                                                                        CORE_SIZE__SINGLEMODE
                                                                            ? singleMtjYellow
                                                                            : this.props.wizard.assembly.coreSize ===
                                                                                CORE_SIZE__MULTIMODE_50
                                                                              ? singleMtjBlue
                                                                              : singleMtjRed
                                                                    }
                                                                    alt="Single MTJ end B"
                                                                />
                                                            </div>
                                                        </div>
                                                    </Tooltip>
                                                )}
                                                {this.isDualMTJVisible() && (
                                                    <Tooltip placement="right" title=" Test End A and End B simultaneously">
                                                        <div
                                                            data-testid="selectDualMtj"
                                                            className={
                                                                'mtj-a-and-b' +
                                                                (this.props.wizard.equipment.mtjConfiguration === MTJ__DOUBLE
                                                                    ? ' selected'
                                                                    : '')
                                                            }
                                                            onClick={() => {
                                                                this.handleChangeMjtConfiguration(MTJ__DOUBLE);
                                                            }}
                                                        >
                                                            <div>
                                                                <div className="mtj-title">Dual MTJ</div>
                                                                <div className="mtj-1">MTJ1</div>
                                                                <div className="mtj-2">MTJ2</div>
                                                                <div className="dut">DUT</div>
                                                                <div className="end-a">A</div>
                                                                <div className="end-b">B</div>
                                                                <img
                                                                    src={
                                                                        this.props.wizard.assembly.coreSize ===
                                                                        CORE_SIZE__SINGLEMODE
                                                                            ? doubleMtjYellow
                                                                            : this.props.wizard.assembly.coreSize ===
                                                                                CORE_SIZE__MULTIMODE_50
                                                                              ? doubleMtjBlue
                                                                              : doubleMtjRed
                                                                    }
                                                                    alt="Double MTJ"
                                                                />
                                                            </div>
                                                        </div>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </div>
                                    </React.Fragment>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }
}
